import { BubbleMenuPlugin } from '@tiptap/extension-bubble-menu';
import { Link } from '@tiptap/extension-link';
import { ReactRenderer } from '@tiptap/react';
import type { LinkEditorState } from './LinkEditorBubble';
import { LinkEditorBubble } from './LinkEditorBubble';
import { activeLinkMark, isLinkMarkActive } from './link_marks';

declare module '@tiptap/core' {
   interface Commands<ReturnType> {
      editableLink: {
         showLinkEditor: (state: LinkEditorState) => ReturnType;
         hideLinkEditor: () => ReturnType;
         editLink: () => ReturnType;
         forceShow: () => ReturnType;
      };
   }

   // The mark keeps Link's name, so its storage lives at editor.storage.link.
   // The per-editorid renderer entries make up the rest of the keys.
   interface Storage {
      link: { forceShow: boolean } & Record<string, unknown>;
   }
}

export const EditableLink = Link.extend({
   inclusive: false,
   priority: -10,

   onCreate() {
      const doOnCreate = () => {
         const props = { editor: this.editor };

         const editorid = this.editor.storage.editorid.editorid;
         this.storage[`${editorid}-renderer`] = new ReactRenderer(LinkEditorBubble, {
            props,
            editor: this.editor,
         });

         // Float above sticky page chrome (the step editor's z-10 save bar
         // sat over the bubble); 50 matches the shadcn overlay tier.
         const element = this.storage[`${editorid}-renderer`].element as HTMLElement;
         element.style.zIndex = '50';
         // BubbleMenu measures the wrapper before setting this width itself.
         // Give Floating UI the final size on its first pass so it does not
         // position a phone-sized bubble using the editor container's width.
         element.classList.add('w-max');

         const pluginKey = `bubble-menu-${editorid}`;
         this.storage.bubbleMenu = BubbleMenuPlugin({
            pluginKey,
            editor: this.editor,
            element,
            appendTo: () => document.body,
            shouldShow: () => isLinkMarkActive(this.editor) || this.storage.forceShow,
            options: {
               placement: 'bottom',
               strategy: 'fixed',
               flip: { padding: 8 },
               shift: { padding: 8, crossAxis: true },
               // BubbleMenu makes the wrapper visible before its asynchronous
               // position calculation. Keep that unpositioned frame hidden;
               // updatePosition makes it visible with left/top set.
               onShow: () => {
                  element.style.visibility = 'hidden';
               },
            },
            updateDelay: 50,
         });

         this.editor.registerPlugin(this.storage.bubbleMenu);

         // Preview and edit have different dimensions, but BubbleMenu only
         // repositions for editor transactions and viewport changes. Follow
         // the rendered panel size so opening the form cannot reuse the
         // preview's coordinates outside a narrow viewport.
         const resizeObserver = new ResizeObserver(() => {
            if (!this.editor.isDestroyed) {
               this.editor.view.dispatch(this.editor.state.tr.setMeta(pluginKey, 'updatePosition'));
            }
         });
         resizeObserver.observe(element);
         this.storage[`${editorid}-resizeObserver`] = resizeObserver;
      };

      const waitForEditorContentReady = () => {
         if (this.editor.view?.dom) {
            doOnCreate();
         } else if (!this.editor.isDestroyed) {
            setTimeout(waitForEditorContentReady, 50);
         }
      };

      waitForEditorContentReady();
   },
   onDestroy() {
      const editorid = this.editor.storage.editorid?.editorid;
      const renderer = this.storage[`${editorid}-renderer`];
      // The bubble-menu plugin view is torn down by editor.destroy(); the
      // ReactRenderer root is ours to unmount.
      this.storage[`${editorid}-resizeObserver`]?.disconnect();
      renderer?.destroy();
   },
   addCommands() {
      return {
         ...this.parent?.(),
         showLinkEditor:
            (state?: LinkEditorState) =>
            ({ dispatch }) => {
               const editorid = this.editor.storage.editorid.editorid;
               const renderer = this.storage[`${editorid}-renderer`];
               if (dispatch && renderer) {
                  renderer.ref?.show?.(state);
               }
               return true;
            },
         hideLinkEditor:
            () =>
            ({ dispatch }) => {
               const editorid = this.editor.storage.editorid.editorid;
               const renderer = this.storage[`${editorid}-renderer`];
               if (dispatch && renderer) {
                  renderer.ref?.hide?.();
               }
               return true;
            },
         editLink:
            () =>
            ({ dispatch, commands, editor }) => {
               if (!dispatch) {
                  return true;
               }
               const mark = activeLinkMark(editor);
               if (mark) {
                  commands.extendMarkRange(mark);
               } else {
                  // Mod-k on an unlinked selection: no mark to extend over,
                  // so pin the bubble open to create a new link.
                  commands.forceShow();
               }
               commands.showLinkEditor('edit');
               return true;
            },
         forceShow:
            () =>
            ({ dispatch, editor }) => {
               if (!dispatch) {
                  return true;
               }
               this.storage.forceShow = true;
               // For some reason, this is needed to force the bubble to show
               editor.commands.blur();
               // The blur command uses requestAnimationFrame, so we need to wait for that to finish before refocusing.
               requestAnimationFrame(() => {
                  editor.commands.focus();
               });
               return true;
            },
      };
   },
   addStorage() {
      return {
         renderer: null,
         bubbleMenu: null,
         forceShow: false,
      };
   },
   addKeyboardShortcuts() {
      return {
         'Mod-k': () => this.editor.commands.editLink(),
      };
   },
   onSelectionUpdate() {
      this.storage.forceShow = false;
      if (isLinkMarkActive(this.editor)) {
         this.editor.commands.showLinkEditor('preview');
      } else {
         this.editor.commands.hideLinkEditor();
      }
   },
   addPasteRules() {
      return [];
   },
   parseHTML() {
      // Wikilink anchors are the WikiLink mark's; matching them here too
      // would stack both marks on a pasted [[...]] link.
      return (this.parent?.() ?? []).map(rule =>
         typeof rule.tag === 'string' ? { ...rule, tag: `${rule.tag}:not([data-wikilink])` } : rule
      );
   },
}).configure({ openOnClick: false, linkOnPaste: false, autolink: false });
